export const AGENT_API_VERSION = "0.1.0-experimental";
export const SAVE_SCHEMA_VERSION = 1;
export const RULES_VERSION = "standard-chess/chess.js-1.4.0";

export const LIMITS = Object.freeze({
  inspectRequests: 8,
  historyPage: 20,
  historyPageMax: 100,
  candidatePage: 32,
  candidatePageMax: 128,
  activityPage: 20,
  activityPageMax: 50,
  activityRetained: 128,
  catalogPage: 20,
  catalogPageMax: 50,
  exactAdvance: 8,
  advanceUntil: 256,
  advanceEvents: 64,
  saveBytes: 128 * 1024,
  pgnBytes: 100_000,
  jsonDepth: 12,
  jsonNodes: 4096,
  metadataTags: 8,
  rationaleCharacters: 2000,
  protocolLineBytes: 128 * 1024,
  protocolResponseBytes: 256 * 1024,
  decisions: 512,
  simulatedPlies: 1024,
  repeatedInvalid: 8,
  repeatedRejected: 8,
  stalledDecisions: 16,
});

const square = Object.freeze({ type: "string", pattern: "^[a-h][1-8]$" });
const planHash = Object.freeze({ type: "string", pattern: "^[a-f0-9]{64}$" });

export const CAPABILITY_REGISTRY = Object.freeze({
  new_game: capability(
    "Start a new standard chess match.",
    "both",
    "createSession",
    {},
  ),
  restore_game: capability(
    "Restore a validated portable chess save.",
    "both",
    "restoreSession",
    {},
  ),
  move: capability(
    "Move the side-to-move's piece to a legal destination.",
    "both",
    "act",
    {
      from: { ...square, required: true },
      to: { ...square, required: true },
      promotion: { type: "string", enum: ["q", "r", "b", "n"] },
      planHash,
    },
    {
      example: { type: "move", from: "e2", to: "e4" },
      candidates: true,
      planning: true,
      confirmationRequired: true,
    },
  ),
  undo: capability(
    "Take back the last player decision using the normal match undo policy.",
    "both",
    "act",
    { planHash },
    {
      example: { type: "undo" },
      planning: true,
      confirmationRequired: true,
    },
  ),
  resign: capability(
    "End the match by resigning the player-controlled side.",
    "both",
    "act",
    { planHash },
    {
      example: { type: "resign" },
      planning: true,
      confirmationRequired: true,
    },
  ),
  request_hint: capability(
    "Request the same progressive Stockfish hint available in the human game.",
    "both",
    "inspect",
    {
      detail: {
        type: "string",
        enum: ["concept", "piece", "move"],
        required: true,
      },
    },
    { example: { view: "hint", detail: "concept" } },
  ),
  export_save: capability(
    "Export a portable, versioned save.",
    "both",
    "exportSave",
    {},
  ),
  import_pgn: capability(
    "Import a PGN from the human interface.",
    "human",
    "lifecycle",
    {},
  ),
  export_pgn: capability(
    "Download PGN from the human interface.",
    "human",
    "presentation",
    {},
  ),
  rematch: capability("Start a rematch.", "human", "lifecycle", {}),
  return_home: capability(
    "Return to the home screen.",
    "human",
    "presentation",
    {},
  ),
  board_navigation: capability(
    "Select, focus, drag, or cancel a board square.",
    "human",
    "input",
    {},
  ),
  flip_board: capability(
    "Flip board orientation.",
    "human",
    "presentation",
    {},
  ),
  copy_fen: capability("Copy the visible FEN.", "human", "presentation", {}),
  display_settings: capability(
    "Change coordinates, highlights, or reduced motion.",
    "human",
    "presentation",
    {},
  ),
  audio_settings: capability(
    "Change music, effects, voice, or volume.",
    "human",
    "presentation",
    {},
  ),
  dialogs: capability(
    "Open or close settings, import, result, and about dialogs.",
    "human",
    "presentation",
    {},
  ),
  clear_saved_data: capability(
    "Clear local human saves and preferences.",
    "human",
    "persistence",
    {},
  ),
  reload_app: capability(
    "Reload the graphical application.",
    "human",
    "presentation",
    {},
  ),
  find_candidates: capability(
    "Discover legal move drafts without guessing squares.",
    "agent",
    "findCandidates",
    {},
  ),
  plan_action: capability(
    "Preview a consequential action and receive a confirmation hash.",
    "agent",
    "plan",
    {},
  ),
  validate_action: capability(
    "Validate an action without changing any state.",
    "agent",
    "validate",
    {},
  ),
  advance_game: capability(
    "Advance pending autonomous opponent plies without rendering or delay.",
    "agent",
    "advanceUntil",
    {},
  ),
  finish_run: capability(
    "Finish a bounded JSON-lines run with an evidence-based summary.",
    "agent",
    "protocol",
    {},
  ),
});

export const OBSERVATION_REGISTRY = Object.freeze({
  summary: observation(
    "Current phase, turn, result, requirement, and suggested next calls.",
    {},
  ),
  objectives: observation(
    "Victory, defeat, and completion progress with blockers and evidence.",
    {},
  ),
  position: observation(
    "The complete public 64-square chess position and FEN-visible metadata.",
    {},
  ),
  match: observation(
    "Public mode, sides, opponent, and difficulty information.",
    {},
  ),
  captured: observation("Captured material; fixed maximum 30 pieces.", {}),
  history: observation(
    "Paginated public move history.",
    pageFields(LIMITS.historyPageMax),
  ),
  catalog: observation("Paginated public modes, opponents, and difficulties.", {
    kind: {
      type: "string",
      enum: ["modes", "opponents", "difficulties"],
      required: true,
    },
    ...pageFields(LIMITS.catalogPageMax),
  }),
  recent_activity: observation(
    "Paginated bounded authoritative events and receipts.",
    pageFields(LIMITS.activityPageMax),
  ),
  hint: observation(
    "A player-visible progressive hint without engine scores.",
    {
      detail: {
        type: "string",
        enum: ["concept", "piece", "move"],
        required: true,
      },
    },
  ),
});

export const AUTHORITATIVE_COMMAND_TYPES = Object.freeze([
  "move",
  "undo",
  "resign",
]);
export const STOP_CLASSES = Object.freeze([
  "decision_required",
  "critical_event",
  "milestone",
  "important_result",
  "victory",
  "defeat",
  "draw",
  "max_steps",
]);

function capability(purpose, exposure, endpoint, fields, options = {}) {
  return Object.freeze({
    purpose,
    exposure,
    endpoint,
    fields,
    example: {},
    candidates: false,
    planning: false,
    ...options,
  });
}

function observation(purpose, fields) {
  return Object.freeze({ purpose, fields });
}

function pageFields(maximum) {
  return {
    offset: { type: "integer", minimum: 0, maximum: 100_000 },
    limit: { type: "integer", minimum: 1, maximum },
  };
}

export function validateFields(value, fields, options = {}) {
  const errors = [];
  if (!isPlainObject(value)) {
    return [
      problem("invalid_type", options.label ?? "value", "object", typeof value),
    ];
  }
  const allowed = new Set([...(options.allowed ?? []), ...Object.keys(fields)]);
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      errors.push(
        problem("unknown_field", key, [...allowed].sort(), value[key]),
      );
    }
  }
  for (const [name, spec] of Object.entries(fields)) {
    const present = Object.hasOwn(value, name);
    if (!present && spec.required) {
      errors.push(problem("missing_field", name, spec.type, undefined));
      continue;
    }
    if (!present) {
      continue;
    }
    const actual = value[name];
    if (!matchesType(actual, spec.type)) {
      errors.push(problem("invalid_type", name, spec.type, typeof actual));
      continue;
    }
    if (typeof actual === "number" && !Number.isFinite(actual)) {
      errors.push(problem("non_finite_number", name, "finite number", actual));
    }
    if (spec.enum && !spec.enum.includes(actual)) {
      errors.push(problem("invalid_enum", name, spec.enum, actual));
    }
    if (
      typeof actual === "number" &&
      spec.minimum !== undefined &&
      actual < spec.minimum
    ) {
      errors.push(problem("out_of_range", name, `>= ${spec.minimum}`, actual));
    }
    if (
      typeof actual === "number" &&
      spec.maximum !== undefined &&
      actual > spec.maximum
    ) {
      errors.push(problem("out_of_range", name, `<= ${spec.maximum}`, actual));
    }
    if (
      typeof actual === "string" &&
      spec.pattern &&
      !new RegExp(spec.pattern).test(actual)
    ) {
      errors.push(problem("invalid_format", name, spec.pattern, actual));
    }
    if (
      typeof actual === "string" &&
      spec.maxLength &&
      actual.length > spec.maxLength
    ) {
      errors.push(problem("too_large", name, spec.maxLength, actual.length));
    }
  }
  return errors;
}

export function validateActionShape(action, { confirmation = false } = {}) {
  if (!isPlainObject(action)) {
    return failure("invalid_type", "Action must be a plain object.", [
      problem("invalid_type", "action", "object", typeof action),
    ]);
  }
  if (typeof action.type !== "string") {
    return failure("missing_field", "Action type is required.", [
      problem("missing_field", "type", "string", action.type),
    ]);
  }
  const definition = CAPABILITY_REGISTRY[action.type];
  if (!definition) {
    return failure("unknown_action", `Unknown action type: ${action.type}.`, [
      problem(
        "unknown_action",
        "type",
        AUTHORITATIVE_COMMAND_TYPES,
        action.type,
      ),
    ]);
  }
  if (definition.endpoint !== "act" || definition.exposure === "human") {
    return failure(
      "action_not_exposed",
      `${action.type} is not an agent gameplay action.`,
      [
        problem(
          "action_not_exposed",
          "type",
          AUTHORITATIVE_COMMAND_TYPES,
          action.type,
        ),
      ],
    );
  }
  const fields = { ...definition.fields };
  if (confirmation && definition.confirmationRequired) {
    fields.planHash = { ...fields.planHash, required: true };
  }
  const errors = validateFields(action, fields, { allowed: ["type"] });
  return errors.length
    ? failure(errors[0].code, "Action fields are invalid.", errors)
    : { ok: true, definition };
}

export function failure(code, message, evidence = [], hints = []) {
  return { ok: false, error: { code, message, evidence, hints } };
}

export function problem(code, field, required, current) {
  return { code, field, required, current };
}

export function isPlainObject(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function matchesType(value, type) {
  if (type === "integer") {
    return Number.isInteger(value);
  }
  if (type === "array") {
    return Array.isArray(value);
  }
  if (type === "object") {
    return isPlainObject(value);
  }
  return typeof value === type;
}

export function jsonClone(value) {
  return structuredClone(value);
}

export function canonicalJson(value) {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export async function sha256(value) {
  const bytes = new TextEncoder().encode(
    typeof value === "string" ? value : canonicalJson(value),
  );
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function page(items, offset, limit) {
  const results = items.slice(offset, offset + limit);
  const nextOffset =
    offset + results.length < items.length ? offset + results.length : null;
  return {
    items: jsonClone(results),
    page: {
      offset,
      limit,
      total: items.length,
      nextOffset,
      truncated: nextOffset !== null,
    },
  };
}
